import { useState } from "react";
import { PriceBookWithCounts } from "../../../../types/price-book.type";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import DeletePriceBookDialog from "../DeletePriceBookDialog";
import { buildStoreBlockerMessage } from "../deleteConflict";
import { PriceBookDeleteTarget } from "../types";

interface DeletePriceBookButtonProps {
  /** The stored record the drawer loaded; null while it is still loading. */
  priceBook: PriceBookWithCounts | null;
  disabled: boolean;
  onDeleted: () => void;
  /** The pricebook was switched off instead of deleted. */
  onDeactivated: () => void;
  /** The API refused the delete; the message says what has to happen first. */
  onRefused: (message: string) => void;
}

/** The drawer footer's delete, with the same store rule and dialog as the list row. */
export default function DeletePriceBookButton({
  priceBook,
  disabled,
  onDeleted,
  onDeactivated,
  onRefused,
}: DeletePriceBookButtonProps) {
  const [deleteTarget, setDeleteTarget] =
    useState<PriceBookDeleteTarget | null>(null);

  const storeCount = priceBook?.active_store_count ?? 0;
  // Stores are the only thing a delete has to wait for; the products go with the pricebook.
  const isBlockedByStores = storeCount > 0;
  const deleteHint = isBlockedByStores
    ? buildStoreBlockerMessage(storeCount)
    : "";

  const handleAskDelete = () => {
    if (!priceBook) return;
    setDeleteTarget({
      uuid: priceBook.uuid,
      code: priceBook.code,
      name: priceBook.name,
      status: priceBook.status,
      branchCount: priceBook.branch_count,
      productCount: priceBook.product_count,
      isDraft: priceBook.is_draft,
    });
  };

  const handleCloseDialog = () => {
    setDeleteTarget(null);
  };

  return (
    <>
      <Tooltip title={deleteHint}>
        <span>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={disabled || !priceBook || isBlockedByStores}
            onClick={handleAskDelete}
          >
            ลบ
          </Button>
        </span>
      </Tooltip>

      <DeletePriceBookDialog
        target={deleteTarget}
        onClose={handleCloseDialog}
        onDeleted={onDeleted}
        onDeactivated={onDeactivated}
        onRefused={onRefused}
      />
    </>
  );
}
